import React, { useState, useEffect } from "react";
import { ColumnDef } from "@tanstack/react-table";
import styles from "./styles/Doctor.module.css";
import { Toaster } from "../../components/ui/toaster";
import { useToast } from "../../hooks/use-toast";
import DataTable from "../../shared/DataTable";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/Label";
import { Input } from "../../components/ui/input";
import { DoctorPageSchema } from "../../Valdations/Schema/group.schema";
import { useGroupForm } from "../../hooks/useGroupForm";
import { isViewer } from "../../interface/RoleHelpers";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import { DoctorAxiosInstance } from "../../Network/Doctor";
import { Datum } from "../../interface/Doctor";
import { AxiosError } from "axios";
import FileUpload from "../../UploadFile/doctor";
import { CodeOption } from "@/component/SearchableCodeSelect";
import CodeMultiSelect from "../../component/CodeMultiSelect";
import { canRestoreOrHardDelete } from "../../interface/RoleHelpers";
import { AlertTriangle } from "lucide-react";

const EXPORT_ROW_URL = (id: string | number) => `doctors/${id}/export`;

const saveBlobResponse = (response: any, fallbackName: string) => {
  const disposition: string = response.headers?.["content-disposition"] ?? "";
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const filename = match ? decodeURIComponent(match[1]) : fallbackName;

  const blob = new Blob([response.data], {
    type: response.headers?.["content-type"] || "application/octet-stream",
  });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

const getBlobErrorMessage = async (error: any, fallback: string): Promise<string> => {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      return parsed?.error?.message || parsed?.message || fallback;
    } catch {
      return fallback;
    }
  }
  return data?.error?.message || data?.message || error?.message || fallback;
};

const DoctorPage: React.FC = () => {
  // =====================================
  // Main Data State
  // =====================================
  const [allItems, setAllItems] = useState<Datum[]>([]);

  // =====================================
  // Form Input State
  // =====================================
  const [isActive, setIsActive] = useState<boolean>(true);
  const [SpecialtyOption, setSpecialtyOption] = useState<CodeOption[]>([]);
  const [selectedSpecialtyCode, setSelectedSpecialtyCode] = useState<string>("");

  // =====================================
  // Pagination State
  // =====================================
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // =====================================
  // Form Control Helpers
  // =====================================
  const [submitted, setSubmitted] = useState(false);
  const { values, errors, setField, validateAll } = useGroupForm(DoctorPageSchema);

  // =====================================
  // Selected / Editing State
  // =====================================
  const [warningDialog, setWarningDialog] = useState<{
    open: boolean;
    type: "delete" | "hardDelete" | null;
    item: Datum | null;
    usageData: any;
    loadingUsage: boolean;
  }>({
    open: false,
    type: null,
    item: null,
    usageData: null,
    loadingUsage: false,
  });

  const [editIndex, setEditIndex] = useState<null | number>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Datum | null>(null);
  const [showAllLogs, setShowAllLogs] = useState(false);


  // =====================================
  // Search (server-side)
  // =====================================
  const [advancedSearchInput, setAdvancedSearchInput] = useState({
    name: "",
    specialtyName: "" as string | number,
    licenseNumber: "" as string | number,
    isActive: "",
    isSoftDeleted: "false",
  });
  const [advancedSearch, setAdvancedSearch] = useState({
    name: "",
    specialtyName: "" as string | number,
    licenseNumber: "" as string | number,
    isActive: "",
    isSoftDeleted: "false",
  });

  // =====================================
  // Toast / Notification
  // =====================================
  const { toast } = useToast();

  // =====================================
  // Form Utilities
  // =====================================
  const clear = () => {
    setSubmitted(false);
    setField("name", "");
    setField("licenseNumber", '');
    setSpecialtyOption([]);
    setIsActive(true);
  };

  const handleAdd = () => {
    setEditIndex(null);
    setField("name", "");
    setField("licenseNumber", '');
    setSpecialtyOption([]);
    setIsDialogOpen(true);
  };

  // =====================================
  // Dialog Control Functions
  // =====================================
  const handleOpenDialog = async (item: Datum) => {
    try {
      const response = await DoctorAxiosInstance.get(`doctors/${item.id}`);
      const fullData = response.data.data || response.data;
      setSelectedItem(fullData);
      setIsViewDialogOpen(true);
    } catch (error) {
      console.error("Failed to fetch group details:", error);
      toast({
        title: "Error",
        description: "Failed to load group details. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleCloseDialog = () => {
    setIsViewDialogOpen(false);
    setSelectedItem(null);
    setSubmitted(false);
    clear();
    setShowAllLogs(false);
  };

  const getCreatorName = (item: any): string | null => {
    if (!item) return null;
    if (item.createdBy) {
      const match = item.auditLog?.find((log: any) => log.actorId === item.createdBy);
      if (match) return match.actorName;
      return item.createdBy;
    }
    return null;
  };

  // =====================================
  // Fetch Functions
  // =====================================
  const fetchSpecialityOptions = async (searchTerm: string) => {
    if (!searchTerm || searchTerm.trim().length < 2) return { data: [], total: 0 };
    try {
      const response = await DoctorAxiosInstance.get("specialties/codes", {
        params: { query: searchTerm },
      });

      const items = response.data?.data || [];

      return {
        data: items.map((item: any) => {
          const displayValue = `(${item.code})  ${item.name || ''} `;
          return {
            value: displayValue,
            label: displayValue,
            realId: item.code
          };
        }),
        total: items.length,
      };
    } catch (error) {
      return { data: [], total: 0 };
    }
  };

  const highlightMatch = (text: string, query: string) => {
    if (!query || query.trim().length === 0) return text;

    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();

    if (!lowerText.includes(lowerQuery)) return text;

    const parts = text.split(new RegExp(`(${query})`, "gi"));

    return parts.map((part, index) =>
      part.toLowerCase() === lowerQuery ? (
        <span
          key={index}
          className="bg-yellow-50 text-yellow-900 px-1 rounded font-semibold"
        >
          {part}
        </span>
      ) : (
        part
      ),
    );
  };

  // =====================================
  // Error Handling
  // =====================================
  const getApiErrorMessage = (error: any): string => {
    if (!error) return "Unknown error";

    if (error.response?.data?.error?.message) {
      return error.response.data.error.message;
    }
    if (error.response?.data?.message) {
      return error.response.data.message;
    }

    if (error.message) {
      return error.message;
    }

    return "An unexpected error occurred.";
  };

  // =====================================
  // Filters helper
  // =====================================
  const buildFilterParams = (): Record<string, any> => {
    const params: Record<string, any> = {};
    if (search) params.name = search;
    if (advancedSearch.name) params.name = advancedSearch.name;
    if (advancedSearch.licenseNumber !== "") params.licenseNumber = advancedSearch.licenseNumber;
    if (advancedSearch.specialtyName !== "") params.specialityName = advancedSearch.specialtyName;
    if (advancedSearch.isActive) params.isActive = advancedSearch.isActive === "Active";
    if (advancedSearch.isSoftDeleted !== "") params.isSoftDeleted = advancedSearch.isSoftDeleted === "true";
    return params;
  };

  const hasActiveFilters = Boolean(
    search ||
      advancedSearch.isActive ||
      advancedSearch.isSoftDeleted === "true" ||
      advancedSearch.name ||
      advancedSearch.specialtyName ||
      advancedSearch.licenseNumber,
  );

  // =====================================
  // Fetch Items (Server-side pagination + search + filters)
  // =====================================
  const fetchItems = async () => {
    try {
      const params: Record<string, any> = {
        page,
        limit,
        ...buildFilterParams(),
      };

      const res = await DoctorAxiosInstance.get("doctors", { params });

      const responseData = res.data?.data;
      const items = responseData?.items ?? [];

      setAllItems(items);
      setTotalPages(responseData?.totalPages ?? 1);
      setTotalItems(responseData?.total ?? items.length);

      const pages = responseData?.totalPages ?? 1;
      if (page > pages) {
        setPage(Math.max(1, pages));
      }
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    } 
  };

  // =====================================
  // CRUD Functions
  // =====================================
  const addItem = async (payload: {
    name: string;
    specialityCode: string;
    licenseNumber: number | string;
    isActive: boolean;
  }) => {
    try {
      const response = await DoctorAxiosInstance.post("doctors", payload);

      if (response.data?.success) {
        fetchItems();
        setIsDialogOpen(false);
        clear();
        toast({
          variant: "success",
          title: "Success",
          description: "New Doctor Added Successfully.",
        });
      }
    } catch (error: any) {
      const message = getApiErrorMessage(error);
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    }
  };

  const handleSubmit = async () => {
    setSubmitted(true);

    const isFormValid = validateAll();

    if (!selectedSpecialtyCode) {
      toast({
        title: "Required Field",
        description: "Please select a Speciality.",
        variant: "destructive",
      });
      return;
    }

    if (!isFormValid) return;

    const payload = {
      name: values.name.trim(),
      specialityCode: selectedSpecialtyCode,
      licenseNumber: values.licenseNumber,
      isActive: isActive,
    };

    try {
      await addItem(payload);
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    }
  };

  const handleEdit = (item: Datum) => {
    setField("name", item.name);
    setField("licenseNumber", item.licenseNumber);

    const specialityCode = item.speciality?.code;
    const specialityName = item.speciality?.name;

    if (specialityCode) {
      const displayValue = `(${specialityCode}) ${specialityName || ''}`;
      setSelectedSpecialtyCode(specialityCode.toString());
      setSpecialtyOption([{
        value: specialityCode.toString(),
        label: displayValue,
      }]);
    } else {
      setSelectedSpecialtyCode("");
      setSpecialtyOption([]);
    }

    setIsActive(item.isActive);
    setEditIndex(item.id);
    setIsDialogOpen(true);
  };

  const updateItem = async (id: string | number) => {
    setSubmitted(true);

    const isFormValid = validateAll();
    if (!selectedSpecialtyCode) {
      toast({
        title: "Required Field",
        description: "Please select a Speciality.",
        variant: "destructive",
      });
      return;
    }

    if (!isFormValid) return;

    const payload = {
      name: values.name.trim(),
      specialityCode: selectedSpecialtyCode,
      licenseNumber: values.licenseNumber,
      isActive: isActive,
    };

    try {
      const response = await DoctorAxiosInstance.put(`doctors/${id}`, payload);

      if (response.data?.success) {
        fetchItems();
        setIsDialogOpen(false);
        clear();
        toast({
          variant: "success",
          title: "Success",
          description: "Doctor Updated Successfully.",
        });
      } else {
        toast({
          title: "Error",
          description: response.data?.message || "Failed to Update the Group.",
          variant: "destructive",
        });
      }
    } catch (error: AxiosError | any) {
      const message = getApiErrorMessage(error);
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    }
  };

  const removeItem = async (id: string | number): Promise<void> => {
    try {
      await DoctorAxiosInstance.delete(`doctors/${id}/soft-delete`);
      fetchItems();
      toast({
        variant: "success",
        title: "Success",
        description: "Doctor Deleted Successfully.",
      });
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    }
  };

  const hardDeleteItem = async (id: string | number): Promise<void> => {
    try {
      await DoctorAxiosInstance.delete(`doctors/${id}`);
      await fetchItems();
      toast({
        variant: "success",
        title: "Success",
        description: "Group permanently deleted.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: getApiErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const restoreItem = async (id: string | number): Promise<void> => {
    try {
      await DoctorAxiosInstance.patch(`doctors/${id}/restore`);
      await fetchItems();
      toast({
        variant: "success",
        title: "Success",
        description: "Group restored successfully.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: getApiErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const toggleActive = async (row: Datum) => {
    if (row.isSoftDeleted) return;
    const payload = {
      name: row.name,
      specialityCode: row.speciality?.code,
      licenseNumber: row.licenseNumber,
      isActive: !row.isActive
    };

    try {
      await DoctorAxiosInstance.put(`doctors/${row.id}`, payload);

      toast({
        variant: "success",
        title: "Success",
        description: `Status Updated to ${!row.isActive ? "Active" : "Inactive"}`,
      });

      setAllItems((prev) =>
        prev.map((item) =>
          item.id === row.id ? { ...item, isActive: !item.isActive } : item,
        ),
      );
    } catch (error: any) {
      const validationDetails = error.response?.data?.error?.details;
      let message = "";

      if (validationDetails && Array.isArray(validationDetails) && validationDetails.length > 0) {
        message = validationDetails.map((d: any) => `${d.field}: ${d.message}`).join(", ");
      } else {
        message = getApiErrorMessage(error);
      }

      toast({
        title: "Validation Error",
        description: message,
        variant: "destructive",
      });
    }
  };

  // =====================================
  // Debounce
  // =====================================
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    const t = setTimeout(() => {
      setAdvancedSearch({
        name: advancedSearchInput.name.trim(),
        specialtyName: (advancedSearchInput.specialtyName ?? "").toString().trim(),
        licenseNumber: (advancedSearchInput.licenseNumber ?? "").toString().trim(),
        isActive: advancedSearchInput.isActive,
        isSoftDeleted: advancedSearchInput.isSoftDeleted,
      });
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [advancedSearchInput]);

  // =====================================
  // Fetch on page / limit / search / filters change (server-side, single source of truth)
  // =====================================
  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, search, advancedSearch]);

  // =====================================
  // Download Function
  // =====================================
  const handleDownload = async (item: Datum) => {
    if (isViewer()) {
      toast({
        title: "Error",
        description: "Access denied. Viewers are only allowed to view the system",
        variant: "destructive",
      });
      return;
    }

    try {
      
      const response = await DoctorAxiosInstance.get(EXPORT_ROW_URL(item.id), {
        responseType: "blob",
      });
      saveBlobResponse(response, `Doctor_${item.licenseNumber || "export"}.xlsx`);
      toast({
        variant: "success",
        title: "Export Finished",
        description: `"${item.name}" exported successfully.`,
      });
    } catch (error: any) {
      toast({
        title: "Export Failed",
        description: await getBlobErrorMessage(error, "Failed to export doctor."),
        variant: "destructive",
      });
    } 
  };

  // Table columns
  const columns: ColumnDef<Datum>[] = [
    {
      accessorKey: "name",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="w-full font-bold text-black justify-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Doctor's Name
          {column.getIsSorted() === "asc"
            ? " ↑"
            : column.getIsSorted() === "desc"
              ? " ↓"
              : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div data-testid={`doctor-name-${item.id}`} className={`text-center ${item.isSoftDeleted ? "line-through text-slate-400" : ""}`}>
            {row.getValue("name")}
          </div>
        );
      },
    },
    {
      accessorKey: "licenseNumber",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="w-full font-bold text-black justify-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          License Number
          {column.getIsSorted() === "asc"
            ? " ↑"
            : column.getIsSorted() === "desc"
              ? " ↓"
              : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div
            data-testid={`doctor-license-${item.id}`}
            className={`text-center ${
              item.isSoftDeleted ? "text-slate-400" : ""
            }`}
          >
            {row.getValue("licenseNumber")}
          </div>
        );
      },
    },
    {
      accessorKey: "specialityName",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="w-full font-bold text-black justify-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Speciality
          {column.getIsSorted() === "asc"
            ? " ↑"
            : column.getIsSorted() === "desc"
              ? " ↓"
              : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const originalRow = row.original as any;
        const speciality = originalRow.speciality || {};
        const specialityName = speciality.name || row.getValue("specialityName") || "";
        const isDeleted = speciality.isSoftDeleted === true;
        const isInactive = speciality.isActive === false || originalRow.isActive === false;

        const globalSearch = String(search || "").toLowerCase().trim();
        const advancedSpecialitySearch = String(advancedSearch?.specialtyName || "").toLowerCase().trim();

        const activeSearch = advancedSpecialitySearch.length > 0
          ? advancedSpecialitySearch
          : (globalSearch.length > 0 ? globalSearch : "");

        const hasSearch = activeSearch.length > 0;

        if (!specialityName) {
          return <span className="text-gray-400 text-center block">-</span>;
        }

        let textStyle = "bg-blue-50 text-blue-800 border-transparent";

        if (isDeleted) {
          textStyle = "bg-red-50 border border-red-200 text-red-500 line-through opacity-60";
        } else if (isInactive) {
          textStyle = "bg-gray-100 border border-gray-300 text-gray-500 opacity-70";
        } else if (hasSearch && specialityName.toLowerCase().includes(activeSearch)) {
          textStyle = "bg-yellow-100 border border-yellow-400 scale-105 text-yellow-950 font-semibold";
        } else {
          textStyle = "bg-blue-50 border border-blue-200 text-blue-800";
        }

        return (
          <div className="text-center flex justify-center items-center gap-1.5">
            <div
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 transition-all text-xs font-medium border ${textStyle}`}
            >
              <span>
                {typeof highlightMatch === "function"
                  ? highlightMatch(specialityName, activeSearch)
                  : specialityName
                }
              </span>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "isActive",
      header: () => (
        <div className="w-full font-bold text-black text-center normal-case">
          Status
        </div>
      ),
      cell: ({ row }) => {
        const item = row.original;
        if (item.isSoftDeleted) {
          return (
            <div className="w-full flex justify-center">
              <div className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-200 opacity-40 cursor-not-allowed">
                <span className="inline-block h-4 w-4 transform rounded-full bg-white translate-x-1" />
              </div>
            </div>
          );
        }
        return (
          <div className="w-full flex justify-center">
            <button
              data-testid={`toggle-active-${item.id}`}
              onClick={() => toggleActive(item)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                item.isActive ? "bg-green-500" : "bg-gray-300"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  item.isActive ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>
        );
      },
    },
    {
      id: "view",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="w-full font-bold text-black text-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          View
          {column.getIsSorted() === "asc"
            ? " ↑"
            : column.getIsSorted() === "desc"
              ? " ↓"
              : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="w-full flex justify-center py-2">
            <button
              data-testid={`btn-view-${item.id}`}
              onClick={() => handleOpenDialog(item)}
              className="text-blue-600 hover:text-blue-800"
              title="View Details"
            >
              <i className="fa-solid fa-eye text-lg"></i>
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div data-testid="Doctor-page">
      <div className={styles.container} data-testid="Doctor-page-container">
        <h5 className={styles.title} data-testid="network-page-title">
          <span
            className={styles.dialogIcon}
            data-testid="Doctor-page-title-icon"
          >
            <i className="fa-solid fa-user-doctor"></i>
          </span>
          Doctor 
        </h5>

        <FileUpload
          fetchItems={fetchItems}
          totalItems={totalItems}
          hasFilters={hasActiveFilters}
          exportParams={buildFilterParams()}
        />
      </div>

      {/* search */}
      <div
        className={`${styles["compact-search-container"]} ${styles["mb-6"]}`}
        data-testid="Doctor-page-search-container"
      >
        <div className={styles["search-wrapper"]}>
          <div className={styles["search-header-compact"]}>
            <div className={styles["header-info"]}>
              <i className="fa-solid fa-filter"></i>
              <span>Search Filters</span>
            </div>

            <button
              data-testid="Doctor-page-btn-reset-filters"
              onClick={() => {
                setSearchInput("");
                setAdvancedSearchInput({
                  name: "",
                  specialtyName: "",
                  licenseNumber: "",
                  isActive: "",
                  isSoftDeleted: "false",
                });
              }}
              className={styles["btn-clear-compact"]}
            >
              <i className="fa-solid fa-rotate-left"></i>
              Reset
            </button>
          </div>

          <div
            className={styles["search-fields-compact"]}
            style={{ gridTemplateColumns: "repeat(5, 1fr)" }}
          >
            {/* Doctor's Name */}
            <div className={styles["field-compact"]} data-testid="Doctor-page-search-groupname">
              <label className={styles["label-compact"]}>
                <i className="fa-solid fa-layer-group"></i>
                Doctor's Name
              </label>

              <div className={styles["input-container-compact"]}>
                <input
                  data-testid="Doctor-page-input-search-name"
                  type="text"
                  placeholder="Search by Doctor's Name..."
                  value={advancedSearchInput.name}
                  onChange={(e) =>
                    setAdvancedSearchInput({
                      ...advancedSearchInput,
                      name: e.target.value,
                    })
                  }
                  className={styles["input-compact"]}
                />
              </div>
            </div>

            {/* License Number */}
            <div className={styles["field-compact"]} data-testid="Doctor-page-search-licenseNumber">
              <label className={styles["label-compact"]}>
                <i className="fa-solid fa-layer-group"></i>
                License Number
              </label>

              <div className={styles["input-container-compact"]}>
                <input
                  data-testid="Doctor-page-input-search-licenseNumber"
                  type="text"
                  placeholder="Filter by License Number..."
                  value={advancedSearchInput.licenseNumber ?? ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    setAdvancedSearchInput({
                      ...advancedSearchInput,
                      licenseNumber: val,
                    });
                  }}
                  className={styles["input-compact"]}
                />
              </div>
            </div>

            {/* Speciality */}
            <div className={styles["field-compact"]} data-testid="Doctor-page-search-specialty">
              <label className={styles["label-compact"]}>
                <i className="fa-solid fa-layer-group"></i>
                Speciality
              </label>

              <div className={styles["input-container-compact"]}>
                <input
                  data-testid="Doctor-page-input-search-specialtyName"
                  type="text"
                  placeholder="Filter by Speciality..."
                  value={advancedSearchInput.specialtyName ?? ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    setAdvancedSearchInput({
                      ...advancedSearchInput,
                      specialtyName: val,
                    });
                  }}
                  className={styles["input-compact"]}
                />
              </div>
            </div>

            {/* Status */}
            <div className={styles["field-compact"]} data-testid="Doctor-page-search-status">
              <label className={styles["label-compact"]}>
                <i className="fa-solid fa-toggle-on"></i>
                Status
              </label>
              <div className={styles["select-container-compact"]}>
                <select
                  data-testid="Doctor-page-select-search-status"
                  value={advancedSearchInput.isActive}
                  onChange={(e) =>
                    setAdvancedSearchInput({
                      ...advancedSearchInput,
                      isActive: e.target.value,
                    })
                  }
                  className={styles["input-compact"]}
                >
                  <option value="">All Status</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>

            {/* Deleted Groups */}
            <div
              className={styles["field-compact"]}
              data-testid="doctor-page-search-soft-deleted"
            >
              <label className={styles["label-compact"]}>
                <i className="fa-solid fa-trash-can"></i>
                Deleted Doctors
              </label>
              <button
                data-testid="doctor-page-btn-toggle-soft-deleted"
                type="button"
                onClick={() =>
                  setAdvancedSearchInput({
                    ...advancedSearchInput,
                    isSoftDeleted: advancedSearchInput.isSoftDeleted === "true" ? "false" : "true",
                  })
                }
                className={`${styles["input-compact"]} w-full flex items-center justify-center gap-2 cursor-pointer select-none transition-all ${
                  advancedSearchInput.isSoftDeleted === "true"
                    ? "!bg-red-500 !border-red-600 !text-white font-semibold"
                    : "hover:bg-slate-50 text-slate-600"
                }`}
              >
                <i
                  className={`fa-solid ${
                    advancedSearchInput.isSoftDeleted === "true" ? "fa-rotate-left" : "fa-eye-slash"
                  } text-xs`}
                ></i>
                <span className="text-xs">
                  {advancedSearchInput.isSoftDeleted === "true" ? "Back to List" : "Show "}
                </span>
              </button>
            </div>
          </div>

          <div className={styles["stats-compact"]}>
            <i className="fa-solid fa-chart-bar"></i>
            <span className={styles["stats-text-compact"]} data-testid="Doctor-page-stats">
              Showing <strong>{allItems.length}</strong> of <strong>{totalItems}</strong> groups
            </span>
            {(advancedSearch.isActive ||
              advancedSearch.isSoftDeleted == "true" ||
              advancedSearch.name ||
              advancedSearch.specialtyName || advancedSearch.licenseNumber) && (
              <span className={styles["badge-filtered-compact"]}>
                <i className="fa-solid fa-filter"></i> Filtered
              </span>
            )}
          </div>
        </div>
      </div>

      {/* view dialog */}
      {isViewDialogOpen && selectedItem && (
        <dialog
          className={styles["modern-dialog"]}
          open
          data-testid="doctor-page-dialog"
        >
          <div
            className={styles["dialog-container"]}
            data-testid="doctor-page-dialog-container"
          >
            {/* Header */}
            <div
              className={styles["dialog-header"]}
              data-testid="doctor-page-dialog-header"
            >
              <div className={styles["header-content"]}>
                <div
                  className={styles["header-icon"]}
                  data-testid="doctor-page-dialog-header-icon"
                >
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                    <path
                      d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                  </svg>
                </div>
                <div>
                  <h2
                    className={styles["dialog-title"]}
                    data-testid="doctor-page-dialog-title"
                  >
                    Doctor Details
                  </h2>
                  <p
                    className={styles["dialog-subtitle"]}
                    data-testid="doctor-page-dialog-subtitle"
                  >
                    {selectedItem.name}
                  </p>
                </div>
              </div>

              <button
                className={styles["close-btn"]}
                onClick={handleCloseDialog}
                data-testid="doctor-page-dialog-close-btn"
              >
                ✕
              </button>
            </div>

            {/* Body */}
            <div
              className={styles["dialog-body"]}
              data-testid="doctor-page-dialog-body"
            >
              <section
                className={styles.section}
                data-testid="doctor-page-dialog-basic-info"
              >
                <h3 className={styles["section-title"]}>Basic Information</h3>

                <div className={styles["info-group"]}>
                  <div
                    className={styles["info-row"]}
                    data-testid="doctor-page-dialog-row-name"
                  >
                    <span className={styles["row-label"]}>Doctor Name</span>
                    <span className={styles["info-text"]}>
                      {selectedItem.name}
                    </span>
                  </div>
                  <div
                    className={styles["info-row"]}
                    data-testid="doctor-page-dialog-row-license"
                  >
                    <span className={styles["row-label"]}>License Number</span>
                    <span className={styles["info-text"]}>
                      {selectedItem.licenseNumber}
                    </span>
                  </div>
                  <div className={styles["info-row"]}>
                    <span className={styles["row-label"]}>Speciality</span>
                    <div className="flex flex-wrap gap-3">
                      {selectedItem.speciality ? (
                        (() => {
                          const group = selectedItem.speciality;
                          const softDeleteClasses = group.isSoftDeleted
                            ? "opacity-35 blur-[0.4px] line-through decoration-red-400 border-dashed bg-red-50 text-red-500"
                            : !group.isActive
                            ? styles["badge-inactive"]
                            : styles["badge-active"];

                          return (
                            <div className={`${styles["group-badge-v2"]} ${softDeleteClasses}`}>
                              {group.isSoftDeleted ? (
                                <i className="fa-solid fa-trash-can mr-2 text-xs text-red-500"></i>
                              ) : !group.isActive ? (
                                <i className="fa-solid fa-eye-slash mr-2 text-xs"></i>
                              ) : null}
                              <span className={styles["group-name"]}>{group.name}</span>
                              {group.isSoftDeleted ? (
                                <span className="text-[9px] font-bold bg-red-100 text-red-600 px-1 rounded ml-1.5 tracking-wide">
                                  Deleted
                                </span>
                              ) : !group.isActive ? (
                                <span className={styles["status-text-mini"]}>Inactive</span>
                              ) : null}
                            </div>
                          );
                        })()
                      ) : (
                        "—"
                      )}
                    </div>
                  </div>

                  <div
                    className={styles["info-row"]}
                    data-testid="doctor-page-dialog-row-status"
                  >
                    <span className={styles["row-label"]}>Status</span>
                    <div
                      className={`${styles["status-badge-inline"]} ${
                        selectedItem.isActive ? styles.active : styles.inactive
                      }`}
                      data-testid="doctor-page-dialog-status-badge"
                    >
                      <span className={styles["status-dot"]}></span>
                      {selectedItem.isActive ? "Active" : "Inactive"}
                    </div>
                  </div>
                </div>
              </section>

              {/* ── Audit Trail / Logs ── */}
              <div style={{ padding: "20px 28px 8px" }} data-testid="doctor-page-dialog-audit-trail">
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #bbf7d0" }}>
                    <i className="fa-solid fa-clock-rotate-left" style={{ fontSize: 12, color: "#16a34a" }} />
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Audit Trail</span>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
                  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px" }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Created By</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
                      <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg, #3b82f6, #2563eb)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#fff" }}>
                        {String(getCreatorName(selectedItem) ?? "?")[0].toUpperCase()}
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{getCreatorName(selectedItem) || "—"}</span>
                    </div>
                  </div>

                  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px" }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Created On</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                      <i className="fa-regular fa-calendar" style={{ fontSize: 14, color: "#3b82f6" }} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                        {(selectedItem as any).createdAt
                          ? new Date((selectedItem as any).createdAt).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
                          : "—"}
                      </span>
                    </div>
                  </div>

                  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px" }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Last Updated</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                      <i className="fa-regular fa-clock" style={{ fontSize: 14, color: "#10b981" }} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                        {(selectedItem as any).updatedAt
                          ? new Date((selectedItem as any).updatedAt).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
                          : "—"}
                      </span>
                    </div>
                  </div>
                </div>

                {(selectedItem as any).auditLog?.length > 0 && (
                  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
                      <i className="fa-solid fa-pen-to-square" style={{ fontSize: 11, color: "#10b981" }} />
                      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase" }}>Update History</span>
                      <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#f0fdf4", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0", marginLeft: "auto" }}>
                        {(selectedItem as any).auditLog.length} update{(selectedItem as any).auditLog.length !== 1 ? "s" : ""}
                      </span>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {(selectedItem as any).auditLog
                        .slice(0, showAllLogs ? undefined : 4)
                        .map((log: any, i: number) => (
                          <div key={log.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 8, background: i === 0 ? "#f0fdf4" : "#f8fafc", border: `1px solid ${i === 0 ? "#bbf7d0" : "#e2e8f0"}` }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <div style={{ width: 28, height: 28, borderRadius: "50%", background: i === 0 ? "linear-gradient(135deg, #10b981, #059669)" : "linear-gradient(135deg, #94a3b8, #64748b)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff" }}>
                                {String(log.actorName ?? "?")[0].toUpperCase()}
                              </div>
                              <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{log.actorName}</span>
                              {i === 0 && (
                                <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#dcfce7", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0" }}>
                                  Latest
                                </span>
                              )}
                            </div>
                            <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>
                              {new Date(log.createdAt).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                            </span>
                          </div>
                        ))}

                      {(selectedItem as any).auditLog.length > 4 && (
                        <div style={{ textAlign: "center", padding: "6px 0" }}>
                          <button
                            data-testid="doctor-page-dialog-btn-toggle-logs"
                            onClick={() => setShowAllLogs((v) => !v)}
                            style={{ fontSize: 11, color: "#3b82f6", fontWeight: 600, background: "none", border: "none", cursor: "pointer" }}
                          >
                            {showAllLogs ? "Show less ↑" : `+${(selectedItem as any).auditLog.length - 4} more updates ↓`}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

        
            <div
              className={styles["dialog-footer"]}
              data-testid="doctor-page-dialog-footer"
            >
              <button
                className={styles["btn-secondary"]}
                onClick={handleCloseDialog}
                data-testid="doctor-page-dialog-footer-close-btn"
              >
                Close
              </button>
            </div>
          </div>
        </dialog>
      )}

      {/* Delete & Permanent Delete Warning Dialog */}
      <Dialog
        data-testid="doctorDeleteDialog"
        open={warningDialog.open}
        onOpenChange={(open) =>
          !open &&
          setWarningDialog({
            open: false,
            type: null,
            item: null,
            usageData: null,
            loadingUsage: false,
          })
        }
      >
        <DialogContent
          className="max-w-md bg-white p-0 overflow-hidden rounded-2xl flex flex-col max-h-[90vh]"
          data-testid="page-delete-warning-dialog-content"
        >
          {/* Header Section */}
          <div className="p-6 pb-2 flex-shrink-0">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 font-bold text-red-600">
                <AlertTriangle className="h-5 w-5" />
                {warningDialog.type === "hardDelete" ? "Permanent Deletion" : "Confirm Deletion"}
              </DialogTitle>
              <DialogDescription className="text-slate-500 text-sm mt-1">
                {warningDialog.type === "hardDelete"
                  ? "This will permanently remove the item from the system. This action cannot be undone."
                  : "Are you sure you want to delete this item? This action cannot be undone."}
              </DialogDescription>
            </DialogHeader>
          </div>

          {/* Dynamic Content Body */}
          <div className="px-6 flex-1 min-h-0 flex flex-col overflow-y-auto">
            {warningDialog.type === "hardDelete" && (
              <div className="py-6 flex flex-col items-center justify-center gap-3">
                <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center border-2 border-red-100">
                  <i className="fa-solid fa-trash-can text-red-500 text-xl"></i>
                </div>
                <p className="text-sm text-slate-600 text-center px-4">
                  The item{" "}
                  <strong className="text-slate-800">
                    "{warningDialog.item?.name || "this item"}"
                  </strong>{" "}
                  will be permanently deleted.
                </p>
              </div>
            )}

            {warningDialog.type === "delete" && (
              <div className="py-10 text-center flex flex-col items-center justify-center">
                <div className="bg-slate-50 p-4 rounded-full mb-4">
                  <AlertTriangle className="h-8 w-8 text-slate-300" />
                </div>
                <p className="text-slate-600 text-sm px-4">
                  Are you sure you want to delete{" "}
                  <strong className="text-slate-800">
                    "{warningDialog.item?.name || "this item"}"
                  </strong>
                  ? This item will be soft-deleted and can be restored later.
                </p>
              </div>
            )}
          </div>

          {/* Footer Actions Section */}
          <div className="p-6 pt-4 flex-shrink-0 bg-white border-t mt-auto">
            <DialogFooter className="flex flex-row gap-3 justify-center">
              <Button
                data-testid="warning-dialog-btn-cancel"
                variant="outline"
                className="flex-1 rounded-xl h-10 text-sm"
                onClick={() => setWarningDialog((prev) => ({ ...prev, open: false }))}
              >
                Cancel
              </Button>
              <Button
                data-testid="warning-dialog-btn-confirm"
                className="flex-1 rounded-xl h-10 text-white font-bold text-sm bg-red-500 hover:bg-red-600"
                onClick={async () => {
                  const itemId = warningDialog.item?.id;
                  if (itemId) {
                    if (warningDialog.type === "hardDelete") {
                      await hardDeleteItem(itemId);
                    } else {
                      await removeItem(itemId);
                    }
                  }
                  setWarningDialog((prev) => ({ ...prev, open: false }));
                }}
              >
                {warningDialog.type === "hardDelete" ? "Yes, Permanently Delete" : "Yes, Delete"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            clear();
            setEditIndex(null);
          }
          setIsDialogOpen(open);
        }}
      >
        <DialogContent className={styles.dialogContent} data-testid="Doctor-page-dialogContent">
          <DialogHeader className={styles.dialogHeader}>
            <DialogTitle className={styles.dialogTitle} data-testid="Doctor-form-dialog-title">
              {editIndex === null ? "Add Doctor " : "Edit Doctor "}
            </DialogTitle>
            <DialogDescription className={styles.dialogDescription} data-testid="Doctor-page-dialog-description">
              Fill in the details below to {editIndex === null ? "create" : "update"} a Doctor .
            </DialogDescription>
          </DialogHeader>

          {/* Body */}
          <div className={styles.dialogBody} data-testid="Doctor-page-dialog-body">
            <div className={styles.formGrid}>
              {/* Column 1 */}
              <div>
                <div className={styles.formGroup} data-testid="Doctor-page-input-groupname">
                  <Label htmlFor="name"> Name :</Label>
                  <Input
                    data-testid="Doctor-form-input-name"
                    placeholder="Enter Name..."
                    value={values.name}
                    onChange={(e) => setField("name", e.target.value)}
                  />
                  {submitted && errors.name && (
                    <span className={styles.error} data-testid="Doctor-form-error-name">{errors.name}</span>
                  )}
                </div>

                <div className={styles.formGroup} data-testid="Doctor-page-input-Speciality">
                  <Label className={styles.label} htmlFor="Speciality">
                    Speciality  :
                  </Label>
                  <div style={{ maxWidth: 500 }}>
                    <CodeMultiSelect
                      placeholder="Search By Speciality Name..."
                      value={SpecialtyOption}
                      onChange={(selected: any[]) => {
                        const lastSelection = selected.length > 0 ? [selected[selected.length - 1]] : [];
                        setSpecialtyOption(lastSelection);

                        if (lastSelection.length > 0) {
                          const val = lastSelection[0].value;
                          const match = val.match(/\(([^)]+)\)/);
                          const idOnly = match ? match[1] : val;

                          setSelectedSpecialtyCode(idOnly);
                        } else {
                          setSelectedSpecialtyCode("");
                        }
                      }}
                      fetchOptions={fetchSpecialityOptions}
                    />
                  </div>
                </div>
              </div>

              {/* Column 2 */}
              <div>
                <div className={styles.formGroup} data-testid="Doctor-page-input-licenseNumber">
                  <Label htmlFor="doctorId">License Number: </Label>
                  <Input
                    data-testid="Doctor-form-input-licensenumber"
                    type="text"
                    placeholder="Enter License Number..."
                    value={values.licenseNumber || ""}
                    onChange={(e) => setField("licenseNumber", e.target.value)}
                  />
                  {submitted && errors.licenseNumber && (
                    <span className={styles.error} data-testid="Doctor-form-error-licensenumber">{errors.licenseNumber}</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <DialogFooter className={styles.dialogFooter}>
            <Button
              data-testid="Doctor-form-btn-submit"
              onClick={() => {
                if (editIndex === null) {
                  handleSubmit();
                } else {
                  updateItem(editIndex);
                }
              }}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {editIndex === null ? "Add Doctor" : "Update Doctor"}
            </Button>

            <Button
              data-testid="Doctor-form-btn-cancel"
              className={styles.cancel}
              variant="outline"
              onClick={() => {
                setIsDialogOpen(false);
                setEditIndex(null);
                setSubmitted(false);
                clear();
              }}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Toaster data-testid="Doctor-page-toaster" />

      <div data-testid="Doctor-page-table-wrapper">
        <DataTable
          onAdd={handleAdd}
          onEdit={(item: any) => {
            if (item.isSoftDeleted) return;
            handleEdit(item);
          }}
          onDelete={(id) => {
            const item = allItems.find((i) => i.id === id) ?? null;
            if (item?.isSoftDeleted) return;
            setWarningDialog({
              open: true,
              type: "delete",
              item,
              usageData: null,
              loadingUsage: false,
            });
          }}
          data={allItems}
          columns={columns}
          onDownload={handleDownload}
          addButtonLabel="Add New Doctor"
          pageIndex={page - 1}
          pageSize={limit}
          totalPages={totalPages}
          onPageChange={(newPage) => setPage(newPage + 1)}
          onPageSizeChange={(size) => {
            setLimit(size);
            setPage(1);
          }}
          globalSearch={searchInput}
          onGlobalSearchChange={setSearchInput}
          recoveryActions={(item: any) => {
            if (!canRestoreOrHardDelete()) {
              return (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-slate-400 bg-slate-50 border border-slate-200 px-2 py-1 rounded-lg">
                  <i className="fa-solid fa-lock text-[15px]"></i>
                </span>
              );
            }
            return (
              <div className="flex items-center justify-center gap-1.5">
                <button
                  data-testid={`btn-restore-${item.id}`}
                  onClick={() => restoreItem(item.id)}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-all"
                >
                  <i className="fa-solid fa-rotate-left text-[9px]"></i>
                  Restore
                </button>
                <button
                  data-testid={`btn-hard-delete-${item.id}`}
                  onClick={() => setWarningDialog({
                    open: true,
                    type: "hardDelete",
                    item,
                    usageData: null,
                    loadingUsage: false,
                  })}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 transition-all"
                >
                  <i className="fa-solid fa-trash text-[9px]"></i>
                  Delete
                </button>
              </div>
            );
          }}
          data-testid="doctor-page-datatable"
        />
      </div>
    </div>
  );
};

export default DoctorPage;